import { useCssVar, useMutationObserver } from '@vueuse/core';
import { computed, unref, watch } from 'vue';

const HUBSPOT_TOP_BANNER_OPEN_CLASS = 'hs-web-interactives-top-banner-open';

function noopRef(initialValue) {
  return { value: initialValue };
}

export function useDashTopBannerOffsetManaged() {
  if (typeof document === 'undefined') return noopRef('0px');
  return useCssVar('--dash-top-banner-offset-managed', document.documentElement);
}

export function useDashTopBannerOffset() {
  if (typeof document === 'undefined') return noopRef('0px');
  return useCssVar('--dash-top-banner-offset', document.documentElement);
}

export function useTopBannerOffsetManager({ hubspotBannerHeightPx = 106, enabled = true } = {}) {
  if (typeof document === 'undefined') {
    return {
      apply: () => {},
      managedOffset: noopRef('0px'),
      isHubspotTopBannerOpen: () => false,
    };
  }

  const managedOffset = useDashTopBannerOffsetManaged();
  const isEnabled = computed(() => !!unref(enabled));

  const isHubspotTopBannerOpen = () => {
    const htmlOpen = document.documentElement.classList.contains(HUBSPOT_TOP_BANNER_OPEN_CLASS);
    const bodyOpen = document.body?.classList?.contains?.(HUBSPOT_TOP_BANNER_OPEN_CLASS);
    return htmlOpen || bodyOpen;
  };

  const apply = () => {
    const value =
      isEnabled.value && isHubspotTopBannerOpen() ? `${hubspotBannerHeightPx}px` : '0px';
    document.documentElement.style.setProperty('--dash-top-banner-offset-managed', value);
    managedOffset.value = value;
  };

  useMutationObserver(document.documentElement, apply, {
    attributes: true,
    attributeFilter: ['class'],
  });

  useMutationObserver(
    () => document.body,
    apply,
    {
      attributes: true,
      attributeFilter: ['class'],
    },
    { immediate: true },
  );

  apply();

  watch(isEnabled, apply);

  return {
    apply,
    managedOffset,
    isHubspotTopBannerOpen,
    setManagedOffset: (value) => {
      managedOffset.value = typeof value === 'number' ? `${value}px` : value;
    },
    clearManagedOffset: () => {
      managedOffset.value = '0px';
    },
  };
}
